import React, { ChangeEvent, useState } from 'react'
import {
  FormCheckboxField,
  FormErrorText,
  FormField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ClientAddressDto } from 'api/Clients/Types/ClientDetailedDto'
import { CountryDto } from 'api/Identity/Types/Country'

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  countries: CountryDto[]
  addressees: ClientAddressDto[]
  data: ClientAddressDto
  handleChange(values: ClientAddressDto): void
}

export const AddressModal: React.FC<OuterProps> = ({
  isOpen,
  onClose,
  countries,
  addressees,
  handleChange,
  data,
}) => {
  const [form, setForm] = useState<ClientAddressDto>({
    id: data.id,
    tempId: data.tempId,
    country: data.country,
    postalCode: data.postalCode,
    state: data.state,
    city: data.city,
    street: data.street,
    streetHouseNo: data.streetHouseNo,
    isPrimary: data.isPrimary,
    type: 'Home',
  })
  const [error, setErrors] = useState<string>('')

  const validateForm = () => {
    if (!form.postalCode || !form.street || !form.city || !form.country) {
      setErrors('Please fill required fields.')
      return
    }
    if (!form.isPrimary && addressees.find((x) => x.id !== form.id && !x.isPrimary)) {
      setErrors('You need select at least one Primary Address.')
      return
    }
    handleChange(form)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>{data.id ? 'Edit Address' : 'Add New Address'}</DialogTitle>
        </DialogHeader>
        <div className='grid gap-4 md:grid-cols-2'>
          <FormField
            label='Address Line 1 *'
            value={form.street || ''}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setErrors('')
              setForm({ ...form, street: event.target.value })
            }}
          />
          <FormField
            label='Street Nr'
            value={form.streetHouseNo || ''}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setForm({ ...form, streetHouseNo: event.target.value })
            }
          />
          <FormField
            label='City *'
            value={form.city || ''}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setErrors('')
              setForm({ ...form, city: event.target.value })
            }}
          />
          <FormField
            label='State'
            value={form.state || ''}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setForm({ ...form, state: event.target.value })
            }
          />
          <FormField
            label='Postal Code *'
            value={form.postalCode || ''}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setErrors('')
              setForm({ ...form, postalCode: event.target.value })
            }}
          />
          <FormSelectField
            label='Country *'
            options={countries.map((country) => ({ id: country.id, name: country.name }))}
            value={form.country?.id}
            showSearch
            allowClear
            onChange={(value) => {
              setErrors('')
              const country = countries.find((item) => item.id === value)
              setForm({ ...form, country: country ?? null })
            }}
          />
          <div className='md:col-span-2'>
            <FormCheckboxField
              label='Primary'
              value={form.isPrimary}
              onChange={(checked) => setForm({ ...form, isPrimary: checked })}
            />
          </div>
          <div className='md:col-span-2'>
            <FormErrorText message={error} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} type='button' variant='outline'>
            Cancel
          </Button>
          <Button
            disabled={!form.postalCode || !form.street || !form.city || !form.country}
            type='button'
            onClick={validateForm}
          >
            {data.id ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
